import React, { useEffect, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { faClose, faTableList, faWarning } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { useTranslation } from "react-i18next"
import { Tables } from "../../../Objects/Table/Tables"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { OrderPupop } from "../Order/OrderPupop"
import { OrderPupopClose } from "../Order/OrderPupopClose"
import { RodizioOrderPopup } from "../Order/RodizioOrderPopup"
import { orderMemory } from "../../../Objects/Memory/Memory"
import { CountElements } from "../../../components/CountElements/CountElements"
import GetSpace from "../../../utils/space"
import { useGetTableOpenOrders } from "../../../hooks/Tables/useGetTableOpenOrders"

interface TableOrdersPopupProps {
  tableData: Tables
  onClose: () => void
}

export const TableOrdersPopup: React.FC<TableOrdersPopupProps> = ({ tableData, onClose }) => {
  const { t } = useTranslation("tables")

  const { t: tOrder } = useTranslation("order")

  const { handleGetTableOpenOrders } = useGetTableOpenOrders()

  const [isLoading, setIsLoading] = useState(false)

  const [orders, setOrders] = useState<OrderEntity[]>([])

  const [selectedOrder, setSelectedOrder] = useState<OrderEntity | null>(null)

  const [rodizioRoundOrder, setRodizioRoundOrder] = useState<OrderEntity | null>(null)

  const handleFetchTableOrders = async () => {
    setIsLoading(true)

    try {
      setOrders([])

      await handleGetTableOpenOrders(tableData.id, setOrders)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    handleFetchTableOrders()
  }, [tableData.id])

  return (
    <>
      <ModalPortal>
        <div className={defaultStyle.overlay}>
          <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
            <div className={defaultStyle.alert}>
              <TitlePageResponsiveIconSmall
                iconColor={THEME.colors.iconBGSecondaryColor}
                title={`${t("label_table_relations_title")} #${tableData.number}`}
                icon={faTableList}
                children={
                  <ButtonIcon
                    icon={faClose}
                    size="small"
                    typeButton="button"
                    onClick={onClose}
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    descriptionKey="interactionDescriptions:pages.fluxo.tables.tableOrdersPopup.button.onClose"
                  />
                }
              />

              <GetSpace top={10} />

              {isLoading ? (
                <span className="text-xs text-slate-500">{t("label_table_relations_loading")}</span>
              ) : orders.length === 0 ? (
                <TitlePageResponsiveIconSmall
                  boxShadow="none"
                  icon={faWarning}
                  subtitle={t("label_table_relations_empty")}
                />
              ) : (
                <div className="max-h-[50vh] overflow-auto pr-1">
                  <CountElements
                    count={orders.length}
                    title={t("label_table_orders_count")}
                    animationTypes={AnimationTypes.slideUpIn}
                    animationDuration="2s"
                  />

                  {orders.map((order, index) => (
                    <Animate key={order.id} type={AnimationTypes.fadeInTraslate} duration={`${0.4 + index * 0.25}s`}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedOrder(order)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            setSelectedOrder(order)
                          }
                        }}
                        className="mb-3 w-full cursor-pointer rounded-2xl border border-orange-200 bg-gradient-to-r from-white to-orange-50 p-1 text-left transition-all hover:shadow-md hover:scale-[1.01]"
                        data-interaction-description-key="interactionDescriptions:pages.fluxo.tables.tableOrdersPopup.button.setSelectedOrderOrder"
                      >
                        <div className="rounded-2xl border border-orange-100 bg-white">
                          {orderMemory.getCard(order, index, (rodizioOrder) => setRodizioRoundOrder(rodizioOrder)).children}
                        </div>
                      </div>
                    </Animate>
                  ))}
                </div>
              )}
            </div>
          </Animate>
        </div>
      </ModalPortal>

      {selectedOrder &&
        (selectedOrder.orderPaymentEntity?.isClosed() ? (
          <OrderPupopClose
            order={selectedOrder}
            onClonse={() => setSelectedOrder(null)}
            onSave={handleFetchTableOrders}
          />
        ) : (
          <OrderPupop
            order={selectedOrder}
            title={tOrder("title_popup_edite")}
            onClonse={() => setSelectedOrder(null)}
            onSave={() => {
              setSelectedOrder(null)
              handleFetchTableOrders()
            }}
          />
        ))}

      {rodizioRoundOrder && (
        <RodizioOrderPopup
          order={rodizioRoundOrder}
          onClose={() => {
            setRodizioRoundOrder(null)
            handleFetchTableOrders()
          }}
        />
      )}
    </>
  )
}
